import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import I18nField from './I18nField';
import { createI18nObject } from '../utils/i18n';

export default function MaterialExtraForm({ materialExtra, onSave, onCancel }) {
  const { user } = useAuth();
  const supportedLanguages = user?.supportedLanguages || ['pt'];

  const [formData, setFormData] = useState({
    name: createI18nObject(supportedLanguages),
    key: '',
    filamentType: '',
    materialCostMultiplier: 1,
    description: createI18nObject(supportedLanguages),
    showInSite: true,
    displayOrder: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (materialExtra) {
      setFormData({
        name: materialExtra.name || createI18nObject(supportedLanguages),
        key: materialExtra.key || '',
        filamentType: materialExtra.filamentType || '',
        materialCostMultiplier: materialExtra.materialCostMultiplier || 1,
        description: materialExtra.description || createI18nObject(supportedLanguages),
        showInSite: materialExtra.showInSite ?? true,
        displayOrder: materialExtra.displayOrder || 0
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init form when prop changes
  }, [materialExtra]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSave = {
        ...formData,
        filamentType: formData.filamentType?.trim() ? formData.filamentType.trim().toUpperCase() : null
      };
      await onSave(dataToSave);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save material extra');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'filamentType') {
      setFormData({ ...formData, filamentType: value.toUpperCase() });
      return;
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleI18nChange = (field, lang, value) => {
    setFormData({
      ...formData,
      [field]: { ...formData[field], [lang]: value }
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">
            {materialExtra ? 'Edit Material Extra' : 'Add Material Extra'}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Name */}
            <I18nField
              label="Name"
              value={formData.name}
              onChange={(lang, value) => handleI18nChange('name', lang, value)}
              languages={supportedLanguages}
              required
              disabled={loading}
              maxLength={100}
            />

            {/* Key and Material Cost Multiplier */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="key" className="block text-sm font-medium text-slate-700 mb-2">
                  Key *
                </label>
                <input
                  type="text"
                  id="key"
                  name="key"
                  value={formData.key}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={40}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="filamentType" className="block text-sm font-medium text-slate-700 mb-2">
                  Filament Type
                </label>
                <input
                  type="text"
                  id="filamentType"
                  name="filamentType"
                  value={formData.filamentType}
                  onChange={handleChange}
                  disabled={loading}
                  maxLength={50}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 uppercase"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Optional suffix for slicer filament type matching (e.g. CF, SILK)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="materialCostMultiplier" className="block text-sm font-medium text-slate-700 mb-2">
                  Material Cost Multiplier *
                </label>
                <input
                  type="number"
                  id="materialCostMultiplier"
                  name="materialCostMultiplier"
                  value={formData.materialCostMultiplier}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  min={1}
                  max={10}
                  step={0.01}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Multiplier applied to material cost (1.0 - 2.0)
                </p>
              </div>
            </div>

            {/* Description */}
            <I18nField
              label="Description"
              value={formData.description}
              onChange={(lang, value) => handleI18nChange('description', lang, value)}
              languages={supportedLanguages}
              type="textarea"
              required
              disabled={loading}
              rows={3}
              maxLength={1000}
            />

            {/* Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="displayOrder" className="block text-sm font-medium text-slate-700 mb-2">
                  Display Order *
                </label>
                <input
                  type="number"
                  id="displayOrder"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  min={0}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="showInSite"
                    checked={formData.showInSite}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Show in Site</span>
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Saving...' : 'Save Material Extra'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
